import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Zap, Crown, Check, Star, TrendingUp } from "lucide-react";

const formatBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function Payments() {
  const { user, refresh } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const isSuccess = loc.pathname.endsWith("/sucesso");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/packages");
        setPackages(data);
      } catch (err) {
        logger.error("payments: load packages", err);
      }
    })();
  }, []);

  // If on /sucesso, poll status (Stripe uses session_id; MP uses ref)
  useEffect(() => {
    if (!isSuccess) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const gateway = params.get("gateway");
    const ref = params.get("ref");
    const isMP = gateway === "mp" && ref;
    if (!sessionId && !isMP) return;

    const statusUrl = isMP ? `/payments/mp/status/${ref}` : `/payments/status/${sessionId}`;
    let attempts = 0;
    const maxAttempts = 15;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(statusUrl);
        if (data.payment_status === "paid") {
          clearInterval(iv);
          toast.success("Pagamento confirmado! 🎉");
          await refresh();
          setTimeout(() => nav("/pagamentos", { replace: true }), 1200);
        } else if (data.status === "expired" || data.status === "cancelled" || data.status === "rejected" || attempts >= maxAttempts) {
          clearInterval(iv);
          if (data.status === "pending" || data.status === "in_process") {
            toast.info("Pagamento em processamento. Você será notificado quando confirmar.");
          } else {
            toast.error("Não foi possível confirmar o pagamento");
          }
        }
      } catch (err) {
        logger.error("poll status", err);
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [isSuccess, nav, refresh]);

  const buyStripe = async (packageId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar pagamento");
    }
    setLoading(false);
  };

  const buyMP = async (packageId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/mp/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar Mercado Pago");
    }
    setLoading(false);
  };

  const buy = buyStripe; // default

  if (!user) return null;

  const creditPkgs = packages.filter((p) => p.type === "credits");
  const premiumPkgs = packages.filter((p) => p.type === "premium");

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-24" data-testid="payments-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-primary font-bold">Impulsione seu negócio</p>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
          Pagamentos
        </h1>
      </div>

      {isSuccess && (
        <div className="mb-4 bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-4 text-center" data-testid="payments-success-banner">
          <p className="font-heading font-bold">Confirmando seu pagamento...</p>
        </div>
      )}

      {/* User status */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3" data-testid="credits-card">
          <Star size={22} className="text-primary" />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Créditos de destaque</p>
            <p className="font-heading font-black text-2xl">{user.boost_credits || 0}</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3" data-testid="premium-card">
          <Crown size={22} className={user.is_premium ? "text-primary" : "text-muted-foreground"} />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Plano Premium</p>
            <p className="font-heading font-black text-xl">
              {user.is_premium ? "Ativo" : "Free"}
            </p>
          </div>
        </div>
      </div>

      {/* Boost packages */}
      <h2 className="font-heading font-bold text-xl mb-3 flex items-center gap-2">
        <Zap size={18} className="text-primary" /> Créditos de destaque
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Use créditos para destacar seus pedidos no topo do feed dos vendedores.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        {creditPkgs.map((p) => (
          <div
            key={p.id}
            data-testid={`pkg-${p.id}`}
            className={`bg-card border rounded-2xl p-5 flex flex-col ${
              p.id === "boost_10" ? "border-primary ring-2 ring-primary/30" : "border-border"
            }`}
          >
            {p.id === "boost_10" && (
              <span className="self-start mb-2 text-[10px] uppercase tracking-widest font-bold bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                Mais popular
              </span>
            )}
            <p className="font-heading font-black text-3xl">{p.credits}</p>
            <p className="text-sm text-muted-foreground mb-3">destaques</p>
            <p className="font-mono font-bold text-2xl text-primary">{formatBRL(p.amount)}</p>
            <p className="text-xs text-muted-foreground mb-4">
              {formatBRL(p.amount / p.credits)} cada
            </p>
            <button
              onClick={() => buyMP(p.id)}
              disabled={loading}
              data-testid={`buy-mp-${p.id}`}
              className="h-11 rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90 disabled:opacity-50 mb-2"
            >
              {loading ? "..." : "💚 PIX / Boleto / Cartão"}
            </button>
            <button
              onClick={() => buyStripe(p.id)}
              disabled={loading}
              data-testid={`buy-${p.id}`}
              className="mt-auto h-10 rounded-xl border border-border font-medium text-sm hover:bg-secondary disabled:opacity-50"
            >
              💳 Cartão internacional (Stripe)
            </button>
          </div>
        ))}
      </div>

      {/* Premium */}
      <h2 className="font-heading font-bold text-xl mb-3 flex items-center gap-2">
        <Crown size={18} className="text-primary" /> Plano Premium
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Selo verificado, aparece primeiro nos resultados, badge no perfil.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {premiumPkgs.map((p) => (
          <div
            key={p.id}
            data-testid={`pkg-${p.id}`}
            className="bg-gradient-to-br from-primary/10 to-transparent border border-primary/30 rounded-2xl p-5 flex flex-col"
          >
            <p className="font-heading font-black text-2xl">{p.name}</p>
            <p className="font-mono font-bold text-3xl text-primary mt-1">{formatBRL(p.amount)}</p>
            <p className="text-xs text-muted-foreground mb-4">
              {p.days === 30 ? "por mês" : `${p.days} dias (economize 17%)`}
            </p>
            <ul className="text-sm space-y-1.5 mb-4">
              <li className="flex items-center gap-2"><Check size={14} className="text-primary" /> Selo Premium no perfil</li>
              <li className="flex items-center gap-2"><Check size={14} className="text-primary" /> +1 destaque grátis por mês</li>
              <li className="flex items-center gap-2"><Check size={14} className="text-primary" /> Prioridade nas notificações</li>
              <li className="flex items-center gap-2"><TrendingUp size={14} className="text-primary" /> Aparece antes no feed</li>
            </ul>
            <button
              onClick={() => buyMP(p.id)}
              disabled={loading}
              data-testid={`buy-mp-${p.id}`}
              className="h-11 rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90 disabled:opacity-50 mb-2"
            >
              {loading ? "..." : "💚 Assinar via PIX / Boleto / Cartão"}
            </button>
            <button
              onClick={() => buyStripe(p.id)}
              disabled={loading}
              data-testid={`buy-${p.id}`}
              className="mt-auto h-10 rounded-xl border border-border font-medium text-sm hover:bg-secondary disabled:opacity-50"
            >
              💳 Cartão internacional (Stripe)
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
